import { useQuery, UseQueryResult } from "@tanstack/react-query";
import assertNever from "assert-never";
import React from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import { callNumber } from "../../external_link";
import useCaregiver from "../../hooks/useCaregiver";
import useMixin from "../../hooks/useMixin";
import useTimeSlot from "../../hooks/useTimeSlot";
import { isLoading } from "../../Loading";
import {
  AddressComponents,
  AgencyId,
  GetPatientTimeSlotsResponse,
  LocalDateTime,
  PatientId,
  PatientTaskBroadcastDetails,
  PatientTaskInstanceId,
} from "../../messages";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { AgencyMember } from "../../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { AppChatState } from "../../system/AppState/ChatState";
import { globalize, translate } from "../../translate";
import { getLocationDisplayLocation } from "../../utils/addressComponentUtils";
import { ModalActions, useModalV2 } from "../AppModals";
import { AppLocationModal } from "../Generic/AppLocationModal";
import AppModal from "../Generic/AppModal/AppModal";
import { IsDefined, isDefined } from "../Generic/IsDefined";
import LoadingScaffold from "../LoadingScaffold";
import DirectionsBottomSheet from "../Patient/components/DirectionsBottomSheet";
import { useCallNumber } from "../Patient/useCallNumber";
import useAvailableTaskEngagement from "../useAvailableTaskEngagement";
import useDirections from "../useDirections";
import { useMedflytAgencyId } from "../withMedflytAgencyId";
import { AvailableTaskScreen } from "./AvailableTaskScreen";
import { AvailableTaskV2Screen } from "./AvailableTaskV2Screen";
import useScheduleTask from "./hooks/useScheduleTask";

interface Props {
  appState: AppState;
  api: API;
  patientTaskBroadcastId: PatientTaskInstanceId;
  onClickChatWithAgency: (patientId: PatientId) => void;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
}

type AvailableTaskState =
  | {
      type: "Loading";
    }
  | {
      type: "Ready";
      caregiver: AppCaregiver;
      taskBroadcast: PatientTaskBroadcastDetails;
      agencyMember: AgencyMember;
      countNewPatientChatMessages: number;
      isLocationModalOpen: boolean;
    };

const initialState: AvailableTaskState = { type: "Loading" };

const AvailableTaskActivity = (props: Props) => {
  const [state, setState] = React.useState<AvailableTaskState>(initialState);

  const mixin = useMixin(props.appState);
  const caregiver = useCaregiver(props.appState);

  const handleCloseLocationModal = () => {
    setState((prev) => ({
      ...prev,
      isLocationModalOpen: false,
    }));
  };

  const handleOpenLocationModal = () => {
    setState((prev) => ({
      ...prev,
      isLocationModalOpen: true,
    }));
  };

  React.useEffect(() => {
    if (
      isLoading(caregiver) ||
      isLoading(mixin.appTaskBroadcastsState) ||
      isLoading(mixin.appAgenciesState)
    ) {
      return;
    }

    const taskBroadcast = mixin.appTaskBroadcastsState.tasks.find(
      (x) => x.taskInstanceId === props.patientTaskBroadcastId
    );

    if (!isDefined(taskBroadcast)) {
      console.log(
        `Task broadcast id ${props.patientTaskBroadcastId} could not be found. redirecting back.`
      );
      return props.onClickGoBack();
    }

    const agencyMember = mixin.appAgenciesState.allAgencyMembers.get(taskBroadcast.createdBy);

    if (!isDefined(agencyMember)) {
      console.log(
        `Agency member of task broadcast id ${props.patientTaskBroadcastId} could not be found. redirecting back.`
      );
      return props.onClickGoBack();
    }

    const countNewPatientChatMessages = AppChatState.patientNumNewChatMessages(
      mixin.appChatState,
      taskBroadcast.patientId
    );

    setState({
      type: "Ready",
      caregiver: caregiver,
      taskBroadcast: taskBroadcast,
      agencyMember: agencyMember,
      countNewPatientChatMessages: countNewPatientChatMessages,
      isLocationModalOpen: false,
    });
  }, [caregiver, mixin.appAgenciesState, mixin.appChatState, mixin.appTaskBroadcastsState, props]);

  if (isLoading(caregiver)) {
    return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
  }

  switch (state.type) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "Ready":
      return (
        <AvailableTaskActivityLoaded
          {...props}
          state={state}
          caregiver={caregiver}
          onCloseLocationModal={handleCloseLocationModal}
          onOpenLocationModal={handleOpenLocationModal}
        />
      );
    default:
      assertNever(state);
  }
};

function AvailableTaskActivityLoaded(
  props: Props & {
    state: AvailableTaskState & { type: "Ready" };
    caregiver: AppCaregiver;
    onCloseLocationModal: () => void;
    onOpenLocationModal: () => void;
  }
) {
  const { state } = props;
  const { openLocation, closeLocation, directionsState } = useDirections();

  const medflytAgencyId = useMedflytAgencyId();

  const handleClickLocation = (location: AddressComponents) => {
    openLocation([location.location.lat, location.location.lng]);
  };

  const hanldeGoBackAndRefreshTaskBroadcasts = () => {
    props.appState.refreshTaskBroadcasts();
    props.appState.refreshPatientTasks();
    props.onClickGoBack();
  };

  const { closeEngagementModal, engagementModalState, promptRequestTaskBroadcast } =
    useAvailableTaskEngagement({
      api: props.api,
      taskBroadcastId: props.patientTaskBroadcastId,
      goBackAndRefreshTaskBroadcasts: hanldeGoBackAndRefreshTaskBroadcasts,
      onAssignSuccess: () => {
        props.appState.refreshPatients();
      },
    });

  const handleClickApprove = () => {
    promptRequestTaskBroadcast();
  };

  const handleClickCall = (phoneNumber: string | null) => {
    if (phoneNumber !== null) {
      callNumber(phoneNumber);
    }
  };

  return (
    <>
      {props.caregiver.shouldShowMainV2Screen && !isLoading(medflytAgencyId) ? (
        <AvailableTaskActivityLoadedMedflyt
          {...props}
          agencyId={medflytAgencyId}
          state={state}
          onOpenLocationModal={props.onOpenLocationModal}
          onClickCall={handleClickCall}
        />
      ) : (
        <AvailableTaskScreen
          agencyMember={state.agencyMember}
          caregiver={state.caregiver}
          onClickLocation={props.onOpenLocationModal}
          onClickApprove={handleClickApprove}
          onClickCall={handleClickCall}
          onClickChat={props.onClickChatWithAgency}
          onClickGoBack={props.onClickGoBack}
          taskBroadcastDetails={state.taskBroadcast}
          onRequestNavigate={props.onRequestNavigate}
        />
      )}
      <DirectionsBottomSheet {...directionsState} onRequestClose={closeLocation} />
      <AppModal {...engagementModalState} onRequestClose={closeEngagementModal} />
      <IsDefined value={state.taskBroadcast.patientAddressComponents}>
        {(addressComponents) => (
          <AppLocationModal
            isOpen={state.isLocationModalOpen}
            originLocation={{
              lat: state.caregiver.addressGeoLocation.lat,
              lng: state.caregiver.addressGeoLocation.lng,
            }}
            destination={addressComponents}
            destinationText={getLocationDisplayLocation(addressComponents)}
            onClickDirections={() => handleClickLocation(addressComponents)}
            onRequestClose={props.onCloseLocationModal}
          />
        )}
      </IsDefined>
    </>
  );
}

export type GetTimeSlotsQuery = UseQueryResult<GetPatientTimeSlotsResponse, unknown>;

function AvailableTaskActivityLoadedMedflyt(
  props: Props & {
    agencyId: AgencyId;
    state: AvailableTaskState & { type: "Ready" };
    onOpenLocationModal: () => void;
    onClickCall: (phonenumber: string | null) => void;
  }
) {
  const { state } = props;
  const timeSlot = useTimeSlot();
  const callNumber = useCallNumber({ api: props.api, caregiver: state.caregiver });
  const { createModal } = useModalV2();

  const handleCallPatient = (scheduledDateTime: LocalDateTime) => {
    if (props.state.taskBroadcast.type === "Onboarding") return;
    if (props.state.taskBroadcast.patientPhoneNumber === null) return;

    const dateTimeStr = globalize().formatLocalDateTime(scheduledDateTime, { datetime: "short" });
    const patientName = props.state.taskBroadcast.patientName;

    callNumber({
      name: props.state.taskBroadcast.patientName,
      destination: props.state.taskBroadcast.patientPhoneNumber,
      agencyId: props.state.taskBroadcast.agency.id,
      onClickCall: () => {
        const modal = createModal({
          id: "call-number",
          type: "neutral",
          title: translate(STRINGS.CONFIRM_PATIENT_AVAILABILITY),
          message: translate(STRINGS.CONFIRM_PATIENT_AVAILABILITY_MESSAGE)
            .replace("{{patientName}}", patientName)
            .replace("{{dateTime}}", dateTimeStr),
          actions: [
            {
              children: translate(STRINGS.YES_SCHEDULE_VISIT),
              key: "yes",
              onClick: () => {
                schedule.lateSchedule.mutate(scheduledDateTime);
                modal.remove();
              },
            },
            ModalActions.Close({
              children: translate(STRINGS.DONT_SCHEDULE_VISIT),
              onClick: () => modal.remove(),
            }),
          ],
        });
      },
      onClickCancel: () => schedule.openModal(scheduledDateTime),
    });
  };

  const schedule = useScheduleTask({
    api: props.api,
    taskBroadcast: state.taskBroadcast,
    scheduledDateTime: timeSlot.selectedDateTime,
    onSuccessfulSchedule: () => {
      props.appState.refreshTaskBroadcasts();
      props.appState.refreshPatientTasks();
    },
    onClickCallPatient: handleCallPatient,
    onRequestGoBack: props.onClickGoBack,
  });

  const getTimeSlotsQuery = useQuery({
    queryKey: [
      "time-slots",
      props.agencyId,
      props.state.taskBroadcast.patientId,
      props.state.taskBroadcast.taskInstanceId,
    ],
    queryFn: () =>
      props.api.getPatientTimeSlotsForNurse(
        props.agencyId,
        props.state.taskBroadcast.patientId,
        props.state.taskBroadcast.taskInstanceId
      ),
  });

  const baseProps = {
    agencyMember: state.agencyMember,
    caregiver: state.caregiver,
    onClickLocation: props.onOpenLocationModal,
    onSchedule: schedule.openModal,
    selectedDate: timeSlot.selectedDate,
    selectedTime: timeSlot.selectedTime,
    selectedDateTime: timeSlot.selectedDateTime,
    shouldShowError: timeSlot.shouldShowError,
    shouldShowTimeSlotError: timeSlot.shouldShowError,
    timeSlotAppCardRef: timeSlot.timeSlotAppCardRef,
    selectDateLabelRef: timeSlot.selectDateLabelRef,
    selectTimeLabelRef: timeSlot.selectTimeLabelRef,
    onSelectDate: timeSlot.selectDate,
    onSelectTime: timeSlot.selectTime,
    onShowError: timeSlot.showError,
    onClickCall: props.onClickCall,
    onClickChat: props.onClickChatWithAgency,
    onClickGoBack: props.onClickGoBack,
    taskBroadcastDetails: state.taskBroadcast,
    onRequestNavigate: props.onRequestNavigate,
    onRequestOutsourcingAgencyData: props.api.getOutsourcingAgencyData,
  };

  switch (getTimeSlotsQuery.status) {
    case "error":
    case "loading":
      return <AvailableTaskV2Screen {...baseProps} slotsStatus={getTimeSlotsQuery.status} />;

    case "success":
      return (
        <AvailableTaskV2Screen
          {...baseProps}
          slotsStatus="loaded"
          slots={getTimeSlotsQuery.data.slots}
          message={getTimeSlotsQuery.data.note}
        />
      );
  }
}

export default AvailableTaskActivity;
